import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge, Button, Card, CardBody, Col, Container, Row, Spinner, Table,
} from "reactstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import ResearchDisclaimer from "../../Components/Common/ResearchDisclaimer";
import { listPublicResearch, getPublicResearchFacets } from "../../services/userPredictionService";
import Pagination from "./Pagination";
import FilterBar from "./FilterBar";
import DateFilter, { describeDateFilter } from "./DateFilter";

import RangePercent from "./RangePercent";
import { modelNameOf } from "../../helpers/modelName";
import { VerdictBadge } from "./VerdictBadge";
import ActualChange from "./ActualChange";
import ResultLegend from "./ResultLegend";
/**
 * Research members have published, readable by anyone.
 *
 * The same rows /my-research shows under "Shared by members", without the
 * account. A shared forecast is already readable by whoever holds its link, so
 * a directory of them discloses nothing new — it makes them findable, which is
 * the only way a stranger encounters this product before deciding whether to
 * sign up.
 *
 * Deliberately narrower than /my-research. There is no scorecard, no settle
 * button, no visibility control and no delete: every one of those acts on a
 * record, and a visitor owns none of these. What is left is the table and the
 * filters, which is what a reader wants.
 *
 * Filter state lives in the URL, as it does on /my-research — so a link to
 * "SPY forecasts by About Stock" is a URL that can be shared, which is most of
 * the point of the page being public at all.
 */

const fmtMoney = (v) => (v === null || v === undefined ? "—" : `$${Number(v).toFixed(2)}`);
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : "—");

const LIST_FACETS = ["symbols", "timeframes", "models", "authors"];
const DATE_KEYS = ["createdFrom", "createdTo", "targetFrom", "targetTo"];

const readFiltersFromUrl = (params) => {
  const filters = {};
  LIST_FACETS.forEach((key) => {
    const raw = params.get(key);
    filters[key] = raw ? raw.split(",").filter(Boolean) : [];
  });
  return filters;
};

const readDatesFromUrl = (params) => {
  const dates = {};
  DATE_KEYS.forEach((key) => {
    const raw = params.get(key);
    if (raw) dates[key] = raw;
  });
  const preset = params.get("datePreset");
  if (preset) dates.preset = preset;
  return Object.keys(dates).length ? dates : null;
};


/**
 * The outcome. No grading control here — a visitor cannot settle anything, and
 * a button that always refuses is worse than no button.
 */
const ResultCell = ({ row }) => {
  if (!row.is_scored) {
    const open = new Date(row.valid_until).getTime() > Date.now();
    return (
      <Badge color="light" className="text-body">
        {open ? "Open" : "Awaiting result"}
      </Badge>
    );
  }
  return (
    <>
      <VerdictBadge verdict={row.member_verdict} />
      <span className="text-muted fs-12">{fmtMoney(row.actual_price)}</span>
      <ActualChange row={row} />
    </>
  );
};

/**
 * Rows per page.
 *
 * 25 rather than the old 100: a prompt is answered by five models at once, so
 * this is five whole questions to a page, and the boundary rarely cuts a
 * comparison in half.
 */
const PAGE_SIZE = 25;

const PublicResearch = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState([]);
  // What the server says about the whole filtered set, not just this page.
  const [pageInfo, setPageInfo] = useState({ total: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 1 });
  const [facets, setFacets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  const filters = useMemo(() => readFiltersFromUrl(searchParams), [searchParams]);
  const dates = useMemo(() => readDatesFromUrl(searchParams), [searchParams]);
  // Page 1 is the absence of the parameter, so a plain /research URL is the
  // first page and stays shareable.
  const page = Math.max(parseInt(searchParams.get("page"), 10) || 1, 1);

  const dateSummary = describeDateFilter(dates);
  const anyFilterActive = LIST_FACETS.some((k) => (filters[k] || []).length) || !!dates;

  // The title carries the filter, so a shared link says what it is showing
  // before the page has loaded anything.
  useEffect(() => {
    const bits = [...(filters.symbols || []), ...(filters.authors || [])];
    document.title = bits.length
      ? `${bits.join(", ")} — shared research | ForecastNest`
      : "Shared research | ForecastNest";
  }, [filters]);

  const updateUrl = useCallback((changes) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => {
      const empty = value == null || value === ""
        || (Array.isArray(value) && value.length === 0);
      if (empty) next.delete(key);
      else next.set(key, Array.isArray(value) ? value.join(",") : value);
    });
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Every filter change returns to page 1. Narrowing a 107-row list to three
  // rows while sitting on page 4 otherwise shows an empty table, which reads as
  // "no matches" rather than "you are past the end".
  const setFilters = (next) =>
    updateUrl({
      ...LIST_FACETS.reduce((acc, key) => ({ ...acc, [key]: next[key] || [] }), {}),
      page: null,
    });

  const setDates = (next) => updateUrl({
    ...DATE_KEYS.reduce((acc, key) => ({ ...acc, [key]: (next || {})[key] || null }), {}),
    datePreset: (next || {}).preset || null,
    page: null,
  });

  const clearEverything = () => updateUrl({
    ...LIST_FACETS.reduce((acc, k) => ({ ...acc, [k]: [] }), {}),
    ...DATE_KEYS.reduce((acc, k) => ({ ...acc, [k]: null }), {}),
    datePreset: null,
    page: null,
  });

  const goToPage = (n) => {
    updateUrl({ page: n <= 1 ? null : n });
    // The table is below the filters; without this a reader who pages from the
    // bottom of the list lands at the bottom of the next one.
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      LIST_FACETS.forEach((key) => {
        if ((filters[key] || []).length) params[key] = filters[key].join(",");
      });
      DATE_KEYS.forEach((key) => {
        if (dates && dates[key]) params[key] = dates[key];
      });
      params.limit = PAGE_SIZE;
      if (page > 1) params.page = page;
      const res = await listPublicResearch(params);
      setRows(res.data || []);
      setPageInfo({
        // Falls back to the page length on an API that has not been redeployed,
        // so an old server renders a single correct page rather than "0".
        total: res.total ?? (res.data || []).length,
        page: res.page ?? 1,
        pageSize: res.pageSize ?? PAGE_SIZE,
        totalPages: res.totalPages ?? 1,
      });
      setErrorMsg(null);
    } catch (e) {
      setRows([]);
      setPageInfo({ total: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 1 });
      setErrorMsg(
        (typeof e === "string" && e) || e?.message || "Could not load shared research."
      );
    } finally {
      setLoading(false);
    }
  }, [filters, dates, page]);

  useEffect(() => { load(); }, [load]);

  // The facet catalogue is the same whatever is filtered, so it is fetched once
  // rather than on every filter change.
  useEffect(() => {
    (async () => {
      try {
        const res = await getPublicResearchFacets();
        setFacets(res.data || null);
      } catch {
        setFacets(null);
      }
    })();
  }, []);

  // An explicit paddingTop, not a Bootstrap utility.
  //
  // .page-content sets `padding: calc(70px + 1.5rem) ...` and pt-4 is
  // `padding-top: 1.5rem !important`, so adding the class REPLACED 94px with
  // 24px and pushed the heading further under the bar rather than clear of it.
  // The same trap as `section pt-5 mt-5` on the contact page: these utilities
  // override, they do not add.
  //
  // 118 clears the ~72px fixed navbar with room to spare, and matches the
  // forecast page so the two open routes sit level.
  return (
    <div className="page-content" style={{ paddingTop: 118 }}>
      <Container fluid>
        <Row className="justify-content-center">
          <Col xxl={10}>
            <div className="mb-3">
              <h4 className="mb-1">Shared research</h4>
              <p className="text-muted mb-0">
                Forecasts members have chosen to publish, with what each AI said and
                how it scored. <Link to="/how-scoring-works">How scoring works</Link>.
              </p>
            </div>

            <ResearchDisclaimer className="mb-3" />

            <Card>
              <CardBody>
                <FilterBar
                  facets={facets}
                  selected={filters}
                  onChange={setFilters}
                  loading={loading}
                  extraControls={<DateFilter value={dates} onChange={setDates} loading={loading} />}
                  extraChips={dateSummary && (
                    <Badge color="primary" className="d-inline-flex align-items-center">
                      <span className="opacity-75 me-1">Date:</span>
                      {dateSummary}
                      <button type="button" className="btn-close btn-close-white ms-2"
                              style={{ fontSize: "0.5rem" }}
                              aria-label="Remove date filter"
                              onClick={() => setDates(null)} />
                    </Badge>
                  )}
                />

                {errorMsg ? (
                  <div className="text-center py-4">
                    <p className="text-muted mb-3">{errorMsg}</p>
                    <Button color="light" onClick={load}>Try again</Button>
                  </div>
                ) : loading ? (
                  <div className="text-center py-4"><Spinner /></div>
                ) : rows.length === 0 ? (
                  <div className="text-center py-4">
                    {/* Past the end, rather than nothing to show. A hand-typed
                        or stale ?page= lands here, and "no research matches
                        this filter" would be a lie about the filter. */}
                    {pageInfo.total > 0 ? (
                      <>
                        <p className="text-muted mb-1">
                          There is no page {pageInfo.page}.
                        </p>
                        <p className="text-muted fs-13 mb-3">
                          This list runs to page {pageInfo.totalPages}.
                        </p>
                        <Button color="light" onClick={() => goToPage(1)}>
                          Back to the first page
                        </Button>
                      </>
                    ) : anyFilterActive ? (
                      <>
                        <p className="text-muted mb-3">No shared research matches this filter.</p>
                        <Button color="light" onClick={clearEverything}>Clear filters</Button>
                      </>
                    ) : (
                      <>
                        <p className="text-muted mb-1">Nothing has been shared yet.</p>
                        <p className="text-muted fs-13 mb-3">
                          Members keep their research private by default.
                        </p>
                        <Link to="/my-research/generate" className="btn btn-primary">
                          Record your own
                        </Link>
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    <ResultLegend />
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Symbol</th>
                          <th>Horizon</th>
                          <th>Model</th>
                          <th>Created</th>
                          <th>Forecast range</th>
                          <th>Conf.</th>
                          <th>Target</th>
                          <th>Result</th>
                          <th>By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row) => (
                          <tr key={row.id}
                              onClick={() => navigate(`/prediction/${row.id}`)}
                              style={{ cursor: "pointer" }}
                              title="Open this forecast">
                            <td className="fw-medium">{row.symbol}</td>
                            <td>{row.timeframe}</td>
                            <td>{modelNameOf(row)}</td>
                            <td>{fmtDate(row.input_time)}</td>
                            <td>
                              {fmtMoney(row.range_min)} – {fmtMoney(row.range_max)}
                              <RangePercent row={row} />
                            </td>
                            <td>{row.confidence ? `${Number(row.confidence).toFixed(0)}%` : "—"}</td>
                            <td>{fmtDate(row.valid_until)}</td>
                            <td><ResultCell row={row} /></td>
                            <td className="text-muted">{row.author}</td>
                          </tr>
                        ))}
                      </tbody>
                      </Table>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>

            {!loading && (
              <Pagination
                page={pageInfo.page}
                totalPages={pageInfo.totalPages}
                total={pageInfo.total}
                pageSize={pageInfo.pageSize}
                onPage={goToPage}
                loading={loading}
                noun="shared forecast"
              >
                Open one to see the prompt, every model's answer and the
                reasoning behind it.
              </Pagination>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PublicResearch;
